import type { ColumnDef } from '@tanstack/react-table';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { formatDisplayDate } from '@/utils/date';
import type { InstituteListItem } from '../types/institute.types';

interface InstituteColumnActions {
  onEdit?: (institute: InstituteListItem) => void;
  onDelete?: (institute: InstituteListItem) => void;
}

function isPresentText(value: string | null | undefined): value is string {
  if (!value) return false;
  const trimmed = value.trim();
  return trimmed !== '' && trimmed.toUpperCase() !== 'NULL';
}

export function getInstituteColumns(
  searchText?: string,
  actions?: InstituteColumnActions,
): ColumnDef<InstituteListItem, unknown>[] {
  const hasActions =
    actions !== undefined && (actions.onEdit !== undefined || actions.onDelete !== undefined);

  return [
    createSerialColumn<InstituteListItem>(),
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={isPresentText(row.original.nameBn) ? row.original.nameBn : undefined}
        />
      ),
    },
    {
      accessorKey: 'eiinNumber',
      header: 'EIIN',
      cell: ({ row }) => {
        const eiin = row.original.eiinNumber;
        if (!isPresentText(eiin)) return <EmptyValue />;
        return <span className={CODE_BADGE_CLASS}>{eiin}</span>;
      },
    },
    {
      accessorKey: 'instituteTypeName',
      header: 'Type',
      cell: ({ row }) =>
        isPresentText(row.original.instituteTypeName) ? (
          row.original.instituteTypeName
        ) : (
          <EmptyValue />
        ),
    },
    {
      accessorKey: 'thanaName',
      header: 'Thana',
      cell: ({ row }) =>
        isPresentText(row.original.thanaName) ? row.original.thanaName : <EmptyValue />,
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: ({ row }) => {
        const name = isPresentText(row.original.contactName) ? row.original.contactName : null;
        const number = isPresentText(row.original.contactNumber)
          ? row.original.contactNumber
          : null;
        if (!name && !number) return <EmptyValue />;
        return (
          <StackedCellText
            primary={name ?? <EmptyValue />}
            secondary={number ? <span className="tabular-nums">{number}</span> : undefined}
          />
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.status === 'active'} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {formatDisplayDate(row.original.createdAt)}
        </span>
      ),
    },
    ...(hasActions
      ? [
          createActionsColumn<InstituteListItem>((institute) => (
            <RowEditDeleteButtons
              item={institute}
              itemName={institute.name}
              onEdit={actions?.onEdit}
              onDelete={actions?.onDelete}
            />
          )),
        ]
      : []),
  ];
}
